/**
 * Message lifecycle hooks for Post Office headless consumers.
 *
 * Provides `useMessageViewed` — a single hook that fires viewed and delivery
 * analytics events on mount:
 *   - messageViewed() (legacy in-app lifecycle endpoint)
 *   - messageInstanceViewed() (message-instance-event endpoint)
 *   - messageInstanceDeliveredSuccessfully() (message-instance-event endpoint)
 *
 * This module is hoisted into @atlassian/post-office-context so that platform
 * packages can consume it without cross-workspace @post-office/* imports.
 *
 * Canonical sources this was inlined from:
 *   post-office/packages/clients/message-lifecycle
 *   post-office/packages/clients/message-instance-event-frontend-client
 *   post-office/packages/clients/recommendation-context
 */

import { useCallback, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { fg } from '@atlaskit/platform-feature-flags/fg';

import { useMessagePlacementHooks, usePostOfficeContext } from '../index';

import { useMessageContext } from './message-context';
import { MessageEventClient, InAppMessageEventType } from './message-event-client';
import {
	type AdditionalMetadata,
	type MessageInstanceClickedParams,
	MessageInstanceEventClient,
} from './message-instance-event-client';
import { usePlacementContext } from './placement-context';
import { useRecommendationContext } from './recommendation-context';

export type {
	ActionTypeAttributes,
	AdditionalMetadata,
	ClickedElementAttributes,
	ClickedElementAttributesNotApplicable,
	FrontendMessageInstanceEventWithoutRecipient,
	InAppClickedEvent,
	InAppDeliverySuccessfulEvent,
	InAppViewedEvent,
	MessageInstanceClickedParams,
} from './message-instance-event-client';
export {
	MessageInstanceEventClient,
	notApplicableClickedElement,
} from './message-instance-event-client';
export {
	RecommendationContext,
	RecommendationProvider,
	useRecommendationContext,
} from './recommendation-context';
export type { RecommendationContext as RecommendationContextType } from './recommendation-context';
export { MessageContextProvider, useMessageContext } from './message-context';
export type { MessageContextData, MessageCategory } from './message-context';
export { PlacementProvider, usePlacementContext } from './placement-context';

export class MessageInstanceEventContextError extends Error {
	constructor() {
		super('useMessageInstanceEvent must be used inside a Message template render component.');
	}
}

export class MessageInstanceEventUnknownActionTypeError extends Error {
	constructor() {
		super('Unknown action type');
	}
}

/**
 * The Post Office-owned context every message instance event needs before it can be sent.
 */
const REQUIRED_MESSAGE_INSTANCE_EVENT_CONTEXT = [
	'messageInstanceId',
	'messageTemplateId',
	'placementId',
	'messageCreationType',
	'messageTriggerId',
] as const;

export type RequiredMessageInstanceEventContextField =
	(typeof REQUIRED_MESSAGE_INSTANCE_EVENT_CONTEXT)[number];

export type RequiredMessageInstanceEventContext = {
	messageInstanceId?: string;
	messageTemplateId?: string;
	placementId?: string;
	messageCreationType?: 'implicit' | 'explicit';
	messageTriggerId?: string;
};

/** The same context once every required field is known to be present. */
export type CompleteMessageInstanceEventContext = Required<RequiredMessageInstanceEventContext>;

/**
 * Returns the names of the required context fields that are absent or empty.
 */
export const getMissingMessageInstanceEventContext = (
	context: RequiredMessageInstanceEventContext,
): RequiredMessageInstanceEventContextField[] =>
	REQUIRED_MESSAGE_INSTANCE_EVENT_CONTEXT.filter((field) => !context[field]);

/**
 * Type-guard form of {@link getMissingMessageInstanceEventContext}.
 */
export const hasCompleteMessageInstanceEventContext = (
	context: RequiredMessageInstanceEventContext,
): context is CompleteMessageInstanceEventContext =>
	getMissingMessageInstanceEventContext(context).length === 0;

/**
 * Post Office fires its own operational events on a dedicated channel so that platform
 * health can be dashboarded and alerted on independently of product analytics.
 */
const ANALYTICS_CHANNEL = 'postOffice';

export const MISSING_CONTEXT_ACTION = 'failed';
export const MISSING_CONTEXT_ACTION_SUBJECT = 'messageInstanceEvent';

type ReportMissingContextArgs = {
	/** Which lifecycle event could not be constructed: `clicked`, `viewed`, `deliverySuccessful`. */
	messageEventType: string;
	missingFields: RequiredMessageInstanceEventContextField[];
	messageTemplateId?: string;
	placementId?: string;
	/** Present for `clicked` only, to identify which interaction was lost. */
	interactionIdentifier?: string;
	actionType?: string;
};

/**
 * Reports a message instance event that could not be sent because Post Office-owned context
 * was missing.
 *
 * This lives at the client level, rather than in each caller, so that every lifecycle event
 * is covered by one implementation.
 *
 * The GASv3 event is the source of truth in production, including when the host surface has
 * not mounted a `postOffice` channel listener, in which case `fire()` is a silent no-op. It is
 * gated on `post-office_enable_missing-context-analytic-events` so it can be switched off
 * without a release: this is the failure path of every in-app message instance event.
 *
 * The console message is dev-only and deliberately ungated, matching `useGasv3Listener` in
 * this same package — it gives Message Makers immediate feedback while wiring up
 * instrumentation.
 */
const useReportMissingEventContext = (): ((args: ReportMissingContextArgs) => void) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	return useCallback(
		({
			messageEventType,
			missingFields,
			messageTemplateId,
			placementId,
			interactionIdentifier,
			actionType,
		}: ReportMissingContextArgs) => {
			if (fg('post-office_enable_missing-context-analytic-events')) {
				createAnalyticsEvent({
					eventType: 'operational',
					action: MISSING_CONTEXT_ACTION,
					actionSubject: MISSING_CONTEXT_ACTION_SUBJECT,
					actionSubjectId: messageEventType,
					side: 'client',
					attributes: {
						reason: 'missingContext',
						missingFields,
						messageTemplateId,
						placementId,
						interactionIdentifier,
						actionType,
					},
				}).fire(ANALYTICS_CHANNEL);
			}

			if (process.env.NODE_ENV !== 'production') {
				// eslint-disable-next-line no-console
				console.error(
					`[Post Office] ${messageEventType} was not sent — missing required context: ${missingFields.join(', ')}.`,
				);
			}
		},
		[createAnalyticsEvent],
	);
};

export type MessageInstanceEventHookScope = {
	messageInstanceViewed: (additionalMetadataFromFrontend?: AdditionalMetadata) => Promise<void>;
	messageInstanceClicked: (args: MessageInstanceClickedParams) => Promise<void>;
	messageInstanceDeliveredSuccessfully: (
		additionalMetadataFromFrontend?: AdditionalMetadata,
	) => Promise<void>;
};

/**
 * Exposes functions for updating message instance lifecycle events.
 */
export const useMessageInstanceEvent = (): MessageInstanceEventHookScope => {
	// globalThis resolves to window in the browser, this will therefore attach at window.window, but we should not change until we are sure what is relying on this path.
	if (typeof window !== 'undefined' && !globalThis?.window?.postOfficeMessageInstanceEventClient) {
		// If we are in the browser but there is no window.window
		if (typeof globalThis.window === 'undefined') {
			(globalThis.window as unknown) = {};
		}

		globalThis.window.postOfficeMessageInstanceEventClient = new MessageInstanceEventClient();
	}

	const messageInstanceEventClientRef = useRef(
		globalThis?.window?.postOfficeMessageInstanceEventClient ?? new MessageInstanceEventClient(),
	);

	// This falls back to creation per call in the case window is not defined. It is not the best behaviour as this could cause too many object to be created but is
	// better than an uncontrolled error in SSR. This function needs to be made SSR safe in a safter manner.
	const client: MessageInstanceEventClient = messageInstanceEventClientRef.current;

	const {
		messageInstanceId,
		messageTemplateId,
		transactionAccountId = null,
		triggerId: messageTriggerId,
		messageCreationType,
		journeyId,
		journeyTriggerId,
		analyticsDetails = null,
		configSetId,
	} = useMessageContext();

	const { placementId } = usePlacementContext();
	const browserContext = usePostOfficeContext();
	const recommendationContext = useRecommendationContext();
	const reportMissingEventContext = useReportMissingEventContext();

	const messageInstanceDeliveredSuccessfully = useCallback(
		async (additionalMetadataFromFrontend?: AdditionalMetadata) => {
			const eventContext = {
				messageInstanceId,
				messageTemplateId,
				placementId,
				messageCreationType,
				messageTriggerId,
			};

			if (!hasCompleteMessageInstanceEventContext(eventContext)) {
				reportMissingEventContext({
					messageEventType: 'deliverySuccessful',
					missingFields: getMissingMessageInstanceEventContext(eventContext),
					messageTemplateId,
					placementId,
				});
				return;
			}

			await client.sendMessageInstanceEvent({
				messageEventType: 'deliverySuccessful',
				messageInstanceId: eventContext.messageInstanceId,
				messageTemplateId: eventContext.messageTemplateId,
				deliveryChannel: 'in-app',
				placementId: eventContext.placementId,
				channelType: 'pull',
				context: {
					...browserContext,
					source: eventContext.placementId,
					recommendation: {
						recommendationSessionId: recommendationContext.sessionId ?? '',
						recommendationEntityId: recommendationContext.entityId ?? '',
						itemFeatureSetId: recommendationContext.itemFeatureSetId ?? '',
					},
				},
				additionalMetadataFromFrontend: {
					transactionAccountId,
					...additionalMetadataFromFrontend,
					...analyticsDetails,
				},
				messageTriggerId: eventContext.messageTriggerId,
				journeyId,
				journeyTriggerId,
				messageCreationType: eventContext.messageCreationType,
				configSetId,
				timestamp: Date.now(),
			});
		},
		[
			client,
			browserContext,
			messageInstanceId,
			messageTemplateId,
			placementId,
			messageCreationType,
			messageTriggerId,
			transactionAccountId,
			analyticsDetails,
			recommendationContext.entityId,
			recommendationContext.sessionId,
			recommendationContext.itemFeatureSetId,
			journeyId,
			journeyTriggerId,
			configSetId,
			reportMissingEventContext,
		],
	);

	const messageInstanceViewed = useCallback(
		async (additionalMetadataFromFrontend?: AdditionalMetadata) => {
			const eventContext = {
				messageInstanceId,
				messageTemplateId,
				placementId,
				messageCreationType,
				messageTriggerId,
			};

			if (!hasCompleteMessageInstanceEventContext(eventContext)) {
				reportMissingEventContext({
					messageEventType: 'viewed',
					missingFields: getMissingMessageInstanceEventContext(eventContext),
					messageTemplateId,
					placementId,
				});
				return;
			}

			await client.sendMessageInstanceEvent({
				messageEventType: 'viewed',
				messageInstanceId: eventContext.messageInstanceId,
				messageTemplateId: eventContext.messageTemplateId,
				deliveryChannel: 'in-app',
				placementId: eventContext.placementId,
				channelType: 'pull',
				context: {
					...browserContext,
					source: eventContext.placementId,
					recommendation: {
						recommendationSessionId: recommendationContext.sessionId ?? '',
						recommendationEntityId: recommendationContext.entityId ?? '',
						itemFeatureSetId: recommendationContext.itemFeatureSetId ?? '',
					},
				},
				additionalMetadataFromFrontend: {
					transactionAccountId,
					...additionalMetadataFromFrontend,
					...analyticsDetails,
				},
				messageTriggerId: eventContext.messageTriggerId,
				journeyId,
				journeyTriggerId,
				messageCreationType: eventContext.messageCreationType,
				configSetId,
				timestamp: Date.now(),
			});
		},
		[
			client,
			browserContext,
			messageInstanceId,
			messageTemplateId,
			placementId,
			messageCreationType,
			messageTriggerId,
			transactionAccountId,
			analyticsDetails,
			recommendationContext.entityId,
			recommendationContext.sessionId,
			recommendationContext.itemFeatureSetId,
			journeyId,
			journeyTriggerId,
			configSetId,
			reportMissingEventContext,
		],
	);

	const messageInstanceClicked = useCallback(
		async (params: MessageInstanceClickedParams) => {
			const eventContext = {
				messageInstanceId: params.messageInstanceId || messageInstanceId,
				messageTemplateId: params.messageTemplateId || messageTemplateId,
				placementId,
				messageCreationType: params.messageCreationType || messageCreationType,
				messageTriggerId: params.messageTriggerId || messageTriggerId,
			};

			if (!hasCompleteMessageInstanceEventContext(eventContext)) {
				reportMissingEventContext({
					messageEventType: 'clicked',
					missingFields: getMissingMessageInstanceEventContext(eventContext),
					messageTemplateId: params.messageTemplateId || messageTemplateId,
					placementId,
					interactionIdentifier: params.interactionIdentifier,
					actionType: params.action?.actionType,
				});
				return;
			}

			await client.sendMessageInstanceEvent({
				messageInstanceId: params.messageInstanceId ?? messageInstanceId!,
				messageTemplateId: params.messageTemplateId ?? messageTemplateId!,
				deliveryChannel: 'in-app',
				placementId: eventContext.placementId,
				channelType: 'pull',
				context: {
					...browserContext,
					source: eventContext.placementId,
					recommendation: {
						recommendationSessionId: recommendationContext.sessionId ?? '',
						recommendationEntityId: recommendationContext.entityId ?? '',
						itemFeatureSetId: recommendationContext.itemFeatureSetId ?? '',
					},
				},
				messageCreationType: params.messageCreationType ?? messageCreationType!,
				messageTriggerId: params.messageTriggerId ?? messageTriggerId!,
				journeyId,
				journeyTriggerId,
				configSetId,
				...params,
				additionalMetadataFromFrontend: {
					transactionAccountId,
					...params.additionalMetadataFromFrontend,
					...analyticsDetails,
				},
				timestamp: Date.now(),
			});
		},
		[
			client,
			browserContext,
			messageInstanceId,
			messageTemplateId,
			placementId,
			messageCreationType,
			messageTriggerId,
			transactionAccountId,
			analyticsDetails,
			recommendationContext.entityId,
			recommendationContext.sessionId,
			recommendationContext.itemFeatureSetId,
			journeyId,
			journeyTriggerId,
			configSetId,
			reportMissingEventContext,
		],
	);

	return {
		messageInstanceDeliveredSuccessfully,
		messageInstanceViewed,
		messageInstanceClicked,
	};
};

/**
 * Fires all three "viewed" analytics events for a Post Office in-app message.
 *
 * Call this once on mount inside a component that is only rendered when the
 * choreographer allows the message to show (e.g. inside `ChoreographerGate`).
 */
export const useMessageViewed = (): (() => Promise<void>) => {
	// -- message-lifecycle (legacy) client --
	if (!globalThis.window.postOfficeMessageEventClient) {
		globalThis.window.postOfficeMessageEventClient = new MessageEventClient();
	}
	const legacyClient = globalThis.window.postOfficeMessageEventClient;

	// -- message-instance-event client --
	if (!globalThis.window.postOfficeMessageInstanceEventClient) {
		globalThis.window.postOfficeMessageInstanceEventClient = new MessageInstanceEventClient();
	}
	const instanceClient = globalThis.window.postOfficeMessageInstanceEventClient;

	const {
		messageInstanceId,
		messageTemplateId,
		transactionAccountId,
		analyticsDetails,
		triggerId: messageTriggerId,
		messageCreationType,
		journeyId,
		journeyTriggerId,
		configSetId,
	} = useMessageContext();

	const { placementId } = usePlacementContext();
	const browserContext = usePostOfficeContext();
	const recommendationContext = useRecommendationContext();
	const { markViewedFromPlacement } = useMessagePlacementHooks();
	const reportMissingEventContext = useReportMissingEventContext();

	return async (): Promise<void> => {
		if (!messageInstanceId || !messageTemplateId || !placementId) {
			reportMissingEventContext({
				messageEventType: 'viewed',
				missingFields: (
					[
						['messageInstanceId', messageInstanceId],
						['messageTemplateId', messageTemplateId],
						['placementId', placementId],
					] as const
				)
					.filter(([, value]) => !value)
					.map(([field]) => field),
				messageTemplateId,
				placementId,
			});
			return;
		}

		const sharedContext = {
			...browserContext,
			source: placementId,
			recommendation: {
				recommendationSessionId: recommendationContext.sessionId ?? '',
				recommendationEntityId: recommendationContext.entityId ?? '',
				itemFeatureSetId: recommendationContext.itemFeatureSetId ?? '',
			},
		};

		// 1. Legacy lifecycle event (messageViewed)
		markViewedFromPlacement({ messageInstanceId });
		legacyClient.batchSendEvent({
			messageInstanceId,
			messageTemplateId,
			transactionAccountId,
			type: InAppMessageEventType.Viewed,
			placement: placementId,
			createdAt: Date.now(),
			analyticsDetails,
			context: {
				browser: browserContext,
				recommendation: recommendationContext,
			},
		});

		// 2. messageInstanceViewed
		await instanceClient.sendMessageInstanceEvent({
			messageEventType: 'viewed',
			messageInstanceId,
			messageTemplateId,
			deliveryChannel: 'in-app',
			placementId,
			channelType: 'pull',
			context: sharedContext,
			messageCreationType: messageCreationType ?? 'implicit',
			messageTriggerId: messageTriggerId ?? '',
			journeyId,
			journeyTriggerId,
			configSetId,
			additionalMetadataFromFrontend: {
				...(transactionAccountId != null ? { transactionAccountId } : {}),
				...(analyticsDetails ?? {}),
			},
			timestamp: Date.now(),
		});

		// 3. messageInstanceDeliveredSuccessfully
		if (messageCreationType && messageTriggerId) {
			await instanceClient.sendMessageInstanceEvent({
				messageEventType: 'deliverySuccessful',
				messageInstanceId,
				messageTemplateId,
				deliveryChannel: 'in-app',
				placementId,
				channelType: 'pull',
				context: sharedContext,
				messageCreationType,
				messageTriggerId,
				journeyId,
				journeyTriggerId,
				configSetId,
				additionalMetadataFromFrontend: {
					...(transactionAccountId != null ? { transactionAccountId } : {}),
					...(analyticsDetails ?? {}),
				},
				timestamp: Date.now(),
			});
		}
	};
};
